import { html } from "https://cdn.jsdelivr.net/npm/lit-element@4.1.1/+esm";
import { ShadowDOMElement } from "./fields.js";

/**
 * Container for admission conditions checkboxes and textarea.
 * Validation rules:
 * - At least one checkbox must be checked
 * - If "Autre, précisez" (condition_4) is checked, the textarea is required
 */
export class AdmissionConditionsContainer extends ShadowDOMElement {
	static className = "AdmissionConditionsContainer";
	
	static get properties() {
		return {
			condition4Checked: { type: Boolean, state: true }
		};
	}
	
	constructor() {
		super();
		this.condition4Checked = false;
	}
	
	isTransient() {
		return false;
	}
	
	render() {
		return html`
			<title-field label="Admission aux assemblées générales et exercice du droit de vote *"></title-field>
			
			<row-wrapper>
				<checkbox-field
					label="Dans les conditions statutaires et légales"
					name="condition_1"
					@change="${this.#onCheckboxChange}"
				></checkbox-field>
			</row-wrapper>
			
			<row-wrapper>
				<checkbox-field
					label="Tout associé peut participer aux assemblées sur justification de son identité et de l'inscription en compte de ses actions"
					name="condition_2"
					@change="${this.#onCheckboxChange}"
				></checkbox-field>
			</row-wrapper>
			
			<row-wrapper>
				<checkbox-field
					label="Tout actionnaire peut participer aux assemblées : chaque action donne droit à une voix"
					name="condition_3"
					@change="${this.#onCheckboxChange}"
				></checkbox-field>
			</row-wrapper>
			
			<row-wrapper>
				<checkbox-field
					label="Autre, précisez"
					name="condition_4"
					@change="${this.#onCondition4Change}"
				></checkbox-field>
			</row-wrapper>
			
			<row-wrapper>
				<text-area-field
					label="*"
					name="conditions"
					?required="${this.condition4Checked}"
				></text-area-field>
			</row-wrapper>
		`;
	}
	
	#onCheckboxChange = () => {
		// Clear validation messages when any checkbox changes
		this.#clearValidationMessages();
	}
	
	#onCondition4Change = (event) => {
		this.condition4Checked = event.target.checked;
		this.#clearValidationMessages();
	}
	
	#clearValidationMessages() {
		this.shadowRoot.querySelectorAll('div[data-validation-message]').forEach(el => el.remove());
	}
	
	#showValidationMessage(message) {
		// Remove existing messages first
		this.#clearValidationMessages();
		
		const row = document.createElement('div');
		row.className = 'row align-items-center';
		row.setAttribute('data-validation-message', 'true');

		const colLeft = document.createElement('div');
		colLeft.className = 'col-sm-3';

		const colRight = document.createElement('div');
		colRight.className = 'col-sm-9 text-danger d-flex align-items-center';
		colRight.style.paddingBottom = '0.25rem';

		const icon = document.createElement('i');
		icon.className = 'fa-solid fa-circle-exclamation me-2';
		icon.setAttribute('aria-hidden', 'true');

		const text = document.createElement('span');
		text.textContent = message;

		colRight.appendChild(icon);
		colRight.appendChild(text);

		row.appendChild(colLeft);
		row.appendChild(colRight);

		// Insert after the title
		const title = this.shadowRoot.querySelector('title-field');
		if (title && title.nextSibling) {
			title.parentNode.insertBefore(row, title.nextSibling);
		} else {
			this.shadowRoot.appendChild(row);
		}
	}
	
	validate() {
		const checkboxes = this.shadowRoot.querySelectorAll('checkbox-field');
		const textarea = this.shadowRoot.querySelector('text-area-field');
		
		// Clear existing validation messages
		this.#clearValidationMessages();
		
		// Rule 1: At least one checkbox must be checked
		const anyChecked = Array.from(checkboxes).some(cb => !!cb.checked);
		if (!anyChecked) {
			this.#showValidationMessage('Vous devez cocher au moins une condition');
			return false;
		}
		
		// Rule 2: If condition_4 is checked, textarea must have content
		const condition4 = this.shadowRoot.querySelector('checkbox-field[name="condition_4"]');
		if (condition4 && condition4.checked) {
			const textareaValue = textarea?.value?.trim() || '';
			if (!textareaValue) {
				this.#showValidationMessage('Veuillez préciser la condition');
				// Mark textarea as invalid
				if (textarea) {
					const input = textarea.shadowRoot?.querySelector('textarea');
					if (input) {
						input.classList.add('is-invalid');
						input.classList.remove('is-valid');
						input.setAttribute('aria-invalid', 'true');
						input.style.borderColor = 'red';
					}
				}
				return false;
			}
		}
		
		return true;
	}
	
	toJSON() {
		const checkboxes = this.shadowRoot.querySelectorAll('checkbox-field');
		const textarea = this.shadowRoot.querySelector('text-area-field');
		
		const children = [];
		
		checkboxes.forEach(cb => {
			children.push({
				name: cb.name || cb.getAttribute('name'),
				checked: !!cb.checked
			});
		});
		
		if (textarea) {
			children.push({
				name: textarea.name || textarea.getAttribute('name'),
				value: textarea.value || ''
			});
		}
		
		return {
			class: AdmissionConditionsContainer.className,
			children: children
		};
	}
}

if (typeof window !== 'undefined') {
	window.AdmissionConditionsContainer = AdmissionConditionsContainer;
	customElements.define('admission-conditions-container', AdmissionConditionsContainer);
}
